/**
 * @file Тонкая обёртка над Google Places API (New).
 *
 * Единственное место в проекте, которое ходит в Places: единая строка поиска
 * дополняет нашу выдачу точками с карты и по выбранной точке дотягивает
 * координаты.
 *
 * Legacy-виджет google.maps.places.Autocomplete недоступен для проектов Google
 * Cloud, созданных после марта 2025, поэтому используется AutocompleteSuggestion
 * со своей вёрсткой выпадашки.
 */

// Предел ожидания любого ответа Places. Запрос, повисший без ответа и без
// ошибки, иначе заблокировал бы сабмит (координаты) или показ группы подсказок
// до следующего ввода.
const REQUEST_TIMEOUT = 2000

// Maps грузится вне жизненного цикла Stimulus (shared/_maps_loader), поэтому
// готовность проверяется в момент запроса, а не подпиской на событие.
export function placesAvailable() {
  return Boolean(window.google?.maps?.importLibrary)
}

/**
 * Подсказки локаций.
 *
 * @param {string} query - Строка из поля ввода.
 * @param {{language?: string, locationBias?: object}} options
 * @returns {Promise<Array<{text: string, placeId: string}>>}
 */
export async function fetchPlaceSuggestions(query, options = {}) {
  if (!placesAvailable()) return []

  const { AutocompleteSuggestion } = await google.maps.importLibrary('places')

  const request = { input: query, includedPrimaryTypes: ['(regions)'] }
  const language = options.language || document.documentElement.lang
  if (language) request.language = language
  if (options.locationBias) request.locationBias = options.locationBias

  const { suggestions } = await withTimeout(
    AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
  )

  // Places API (New) разделяет части адреса " - ", а серверный
  // LocationSearchService разбирает запрос по запятым (формат legacy-виджета).
  return suggestions
    .map((suggestion) => suggestion.placePrediction)
    .filter((prediction) => prediction?.text?.text && prediction?.placeId)
    .map((prediction) => ({
      text: prediction.text.text.replaceAll(' - ', ', '),
      placeId: prediction.placeId
    }))
}

/**
 * Координаты выбранной точки. Города, которого нет в нашей БД, на карте для
 * сервера иначе не существует — и «ближайшие клубы» показать не от чего.
 *
 * @param {string} placeId
 * @returns {Promise<{latitude: number, longitude: number}|null>}
 */
export async function fetchPlaceLocation(placeId) {
  if (!placeId || !placesAvailable()) return null

  const { Place } = await google.maps.importLibrary('places')
  const place = new Place({ id: placeId })

  await withTimeout(place.fetchFields({ fields: ['location'] }))

  const location = place.location
  if (!location) return null

  return { latitude: location.lat(), longitude: location.lng() }
}

function withTimeout(promise) {
  let timer = null
  const limit = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('Places request timed out')),
      REQUEST_TIMEOUT
    )
  })

  return Promise.race([promise, limit]).finally(() => clearTimeout(timer))
}

/**
 * Ключ сравнения названий: Places отдаёт «Amsterdam, Noord-Holland, Netherlands»,
 * у нас лежит «Amsterdam». Сравниваем по первой части, без диакритики и
 * пунктуации — иначе «Málaga» и «Malaga» считались бы разными локациями.
 *
 * @param {string} value
 * @returns {string}
 */
export function locationKey(value) {
  return String(value || '')
    .split(',')[0]
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '')
}
